import React, { memo } from 'react';

interface Card {
  suit: '♠' | '♥' | '♦' | '♣';
  rank: 'A' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '10' | 'J' | 'Q' | 'K';
  id: string;
  faceUp?: boolean;
}

interface DemoPlayingCardProps {
  card: Card;
  index?: number;
  className?: string;
}

const DemoPlayingCardComponent: React.FC<DemoPlayingCardProps> = ({ card, index = 0, className = '' }) => {
  const brandLabel = 'JK CASINO';
  const brandSegment = `${brandLabel} • `;
  const repeatCount = 3;
  const brandRingText = Array.from({ length: repeatCount }).fill(brandSegment).join('').trim();
  const ringFontSize = 9;
  const isRed = card.suit === '♥' || card.suit === '♦';
  const color = isRed ? 'text-red-600' : 'text-gray-900';

  const animationDelay = index * 0.1;
  const style = {
    animationDelay: `${animationDelay}s`,
    animationFillMode: 'backwards' as const
  };
  
  if (!card.faceUp) {
    return (
      <div
        className={`relative w-16 h-24 sm:w-20 sm:h-28 md:w-24 md:h-36 rounded-lg shadow-xl animate-deal ${className}`}
        style={style}
        role="img"
        aria-label="Card back"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-800 to-blue-950 rounded-lg border-2 border-blue-600 overflow-hidden">
          <div className="absolute inset-2 border-2 border-blue-400 rounded opacity-50"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-blue-700 rounded-full opacity-30" />
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <svg viewBox="0 0 120 120" className="w-[80%] h-[80%] text-blue-100">
              <defs>
                <path id="backCirclePath" d="M60,60 m-34,0 a34,34 0 1,1 68,0 a34,34 0 1,1 -68,0" />
              </defs>
              <circle cx="60" cy="60" r="34" fill="none" stroke="rgba(96,165,250,0.6)" strokeWidth="2" />
              <text fill="#dbeafe" fontSize={ringFontSize} fontFamily="Inter, 'Segoe UI', sans-serif" fontWeight="800">
                <textPath href="#backCirclePath" startOffset="50%" textAnchor="middle" letterSpacing="1">
                  {brandRingText}
                </textPath>
              </text>
            </svg>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div
      className={`relative w-16 h-24 sm:w-20 sm:h-28 md:w-24 md:h-36 bg-white rounded-lg shadow-xl border-2 border-gray-300 animate-deal ${className}`}
      style={style}
    >
      <div className="absolute top-1 left-1 sm:top-2 sm:left-2">
        <div className={`text-base sm:text-lg md:text-xl font-bold ${color}`}>
          {card.rank}
        </div>
        <div className={`text-lg sm:text-xl md:text-2xl ${color} -mt-1`}>
          {card.suit}
        </div>
      </div>
      
      <div className={`absolute inset-0 flex items-center justify-center text-3xl sm:text-4xl md:text-5xl ${color}`}>
        {card.suit}
      </div>
      
      <div className="absolute bottom-1 right-1 sm:bottom-2 sm:right-2 rotate-180">
        <div className={`text-base sm:text-lg md:text-xl font-bold ${color}`}>
          {card.rank}
        </div>
        <div className={`text-lg sm:text-xl md:text-2xl ${color} -mt-1`}>
          {card.suit}
        </div>
      </div>
    </div>
  );
};

export const DemoPlayingCard = memo(DemoPlayingCardComponent, (prevProps, nextProps) => {
  return (
    prevProps.card.id === nextProps.card.id &&
    prevProps.card.faceUp === nextProps.card.faceUp &&
    prevProps.index === nextProps.index
  );
});

export type { Card };
